import React, {
  FunctionComponent,
  ComponentProps,
  Fragment,
  useCallback,
  useContext
} from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { BreadcrumbsContext } from '../../context/BreadcrumbsContext';
import { Icon, IconSize, IconType } from '../../misc/Icon/Icon';
import { preventEventDefaultHandler } from '../../utils/preventEventDefaultHandler';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import styles from './BreadcrumbBar.module.css';

export type BreadcrumbBarProps = PropsWithoutChildren<ComponentProps<'div'>>;

export const BreadcrumbBar: FunctionComponent<BreadcrumbBarProps> = React.memo(
  (props) => {
    const { className, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const { breadcrumbs } = useContext(BreadcrumbsContext);

    const navigate = useNavigate();
    const handleBackClick = useCallback(() => {
      navigate(-1);
    }, [navigate]);

    return (
      <div
        {...remainingProps}
        className={classes(className, styles.container)}
      >
        <button
          type="button"
          className={classes(styles.iconContainer, styles.backButton)}
          onClick={handleBackClick}
        >
          <Icon
            name="chevron-left"
            type={IconType.Light}
          ></Icon>
        </button>
        {breadcrumbs.map((crumb, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={index}>
            <NavLink
              to={crumb.to}
              className={classes(
                styles.iconContainer,
                styles.crumb,
                crumb.disabled ? styles.crumbDisabled : ''
              )}
              onClick={crumb.disabled ? preventEventDefaultHandler : undefined}
              data-testid="BreadcrumbBar.crumb"
            >
              <Icon
                name={crumb.iconName}
                type={crumb.iconType}
              ></Icon>
            </NavLink>
            {index >= breadcrumbs.length - 1 ? undefined : (
              <div className={styles.iconContainer}>
                <Icon
                  name="caret-right"
                  type={IconType.Solid}
                  size={IconSize.Small}
                ></Icon>
              </div>
            )}
          </Fragment>
        ))}
      </div>
    );
  }
);
